import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import ObsessiveCompulsiveTestForm from "./ObsessiveCompulsiveTestForm";
import { createOcdAssessment } from "../../../store/slices/ocdAssessmentsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice"; // Added this import

const AddObsessiveCompulsiveTest = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        clientName: "",
        diagnosis: "",
        assessedBy: "",
        obsTime: "",
        obsTimeValue: "",
        obsInterference: "",
        obsInterferenceValue: "",
        obsDistress: "",
        obsDistressValue: "",
        obsResistance: "",
        obsResistanceValue: "",
        obsControl: "",
        obsControlValue: "",
        compTime: "",
        compTimeValue: "",
        compInterference: "",
        compInterferenceValue: "",
        compAnxiety: "",
        compAnxietyValue: "",
        compResistance: "",
        compResistanceValue: "",
        compControl: "",
        compControlValue: ""
    });

    // ⭐ ADD THIS HOOK BLOCK HERE ⭐
    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient data for OCD test:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // ensure numeric value for scores
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const mapFormDataToPayload = () => {
        return {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            client_name: formData.clientName,
            diagnosis: formData.diagnosis,
            assessed_by: formData.assessedBy,
            // Obsessions
            obsessions_time_score: parseInt(formData.obsTime || 0, 10),
            obsessions_time_value: formData.obsTimeValue || "",
            obsessions_interference_score: parseInt(formData.obsInterference || 0, 10),
            obsessions_interference_value: formData.obsInterferenceValue || "",
            obsessions_distress_score: parseInt(formData.obsDistress || 0, 10),
            obsessions_distress_value: formData.obsDistressValue || "",
            obsessions_resistance_score: parseInt(formData.obsResistance || 0, 10),
            obsessions_resistance_value: formData.obsResistanceValue || "",
            obsessions_control_score: parseInt(formData.obsControl || 0, 10),
            obsessions_control_value: formData.obsControlValue || "",
            // Compulsions
            compulsions_time_score: parseInt(formData.compTime || 0, 10),
            compulsions_time_value: formData.compTimeValue || "",
            compulsions_interference_score: parseInt(formData.compInterference || 0, 10),
            compulsions_interference_value: formData.compInterferenceValue || "",
            compulsions_anxiety_score: parseInt(formData.compAnxiety || 0, 10),
            compulsions_anxiety_value: formData.compAnxietyValue || "",
            compulsions_resistance_score: parseInt(formData.compResistance || 0, 10),
            compulsions_resistance_value: formData.compResistanceValue || "",
            compulsions_control_score: parseInt(formData.compControl || 0, 10),
            compulsions_control_value: formData.compControlValue || "",
            total_score: Object.values(formData)
                .slice(5) // from obsTime to compControl
                .reduce((sum, val) => sum + (parseInt(val || 0, 10)), 0),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const total = mapFormDataToPayload().total_score;

        if (total < 10) {
            Swal.fire("Incomplete", "Please complete all 10 items.", "warning");
            return;
        }

        try {
            const res = await dispatch(createOcdAssessment(mapFormDataToPayload())).unwrap();
            Swal.fire("Saved!", `Obsessive Compulsive Test completed. Total Score: ${total}`, "success");
            navigate(`/phase04/obsessive-compulsive-test/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            Swal.fire("Error", err || "Failed to save assessment", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <ObsessiveCompulsiveTestForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4 sticky-form-submit">
                                        <i className="ri-save-2-line me-1"></i>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddObsessiveCompulsiveTest;
